/**
 * Worker Kiosk — hamburger side-menu toggle
 * ------------------------------------------------------------------
 * The app shell (`.app-frame` / `.app-sidebar`) is rendered by the shared
 * `@yuksalish/ui` package. On portrait / narrow touch screens `kiosk.css`
 * turns the sidebar into an off-canvas drawer; this module injects the small
 * pieces the drawer needs on top of it:
 *
 *   - a sticky top bar with a hamburger toggle button
 *   - a click-to-close backdrop
 *
 * `kiosk.css` then slides the sidebar in/out via the `body.kiosk-menu-open`
 * class. The toggle/backdrop are hidden on desktop widths by the same sheet.
 *
 * The shared shell only exists after login, so a MutationObserver re-injects
 * if the frame appears later (or after a logout → login round trip).
 */

const MENU_CLASS = 'kiosk-menu-open'

/* Theme support — Light / Dark. The shared shell + kiosk pages are re-themed
   by `html[data-theme="dark"]` rules in `kiosk.css`; this module owns the
   toggle button injected into the sidebar and the localStorage preference. */
const THEME_KEY = 'yuksalish.kiosk.theme'

const SUN_SVG =
  '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41"/></svg>'
const MOON_SVG =
  '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>'

type KioskTheme = 'light' | 'dark'

function currentTheme(): KioskTheme {
  try {
    const stored = localStorage.getItem(THEME_KEY)
    if (stored === 'dark' || stored === 'light') return stored
  } catch {
    /* storage unavailable — fall through */
  }
  try {
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark'
  } catch {
    /* matchMedia unavailable */
  }
  return 'light'
}

/** Label + icon show the mode you switch TO when tapped. */
function themeToggleLabel(theme: KioskTheme): string {
  const target = theme === 'dark' ? 'light' : 'dark'
  const lang = document.documentElement.lang
  if (lang === 'uz') return target === 'dark' ? 'Tungi rejim' : 'Kunduzgi rejim'
  if (lang === 'ru') return target === 'dark' ? 'Тёмная тема' : 'Светлая тема'
  return target === 'dark' ? 'Dark theme' : 'Light theme'
}

function renderThemeToggle(toggle: HTMLButtonElement, theme: KioskTheme): void {
  const target = theme === 'dark' ? 'light' : 'dark'
  toggle.setAttribute('aria-pressed', String(theme === 'dark'))
  toggle.innerHTML =
    `<span class="kiosk-theme-icon" aria-hidden="true">${target === 'dark' ? MOON_SVG : SUN_SVG}</span>` +
    `<span class="kiosk-theme-label">${themeToggleLabel(theme)}</span>`
}

function applyTheme(theme: KioskTheme): void {
  document.documentElement.setAttribute('data-theme', theme)
  try {
    localStorage.setItem(THEME_KEY, theme)
  } catch {
    /* theme still applies for this session */
  }
  document.querySelectorAll<HTMLButtonElement>('.kiosk-theme-toggle').forEach((toggle) => {
    renderThemeToggle(toggle, theme)
  })
}

/** Idempotent: appends the theme toggle to the shared sidebar. It is placed as
 *  a sibling AFTER `.sidebar-user`, i.e. past the end of the Vue vnode list,
 *  so Vue's re-render patch (locale changes, etc.) never touches it. */
function injectThemeToggle(frame: HTMLElement): boolean {
  if (frame.querySelector('.kiosk-theme-toggle')) return true
  const sidebar = frame.querySelector<HTMLElement>('.app-sidebar')
  if (!sidebar) return false

  const toggle = document.createElement('button')
  toggle.type = 'button'
  toggle.className = 'kiosk-theme-toggle'
  renderThemeToggle(toggle, currentTheme())
  toggle.addEventListener('click', () => {
    applyTheme(currentTheme() === 'dark' ? 'light' : 'dark')
  })
  sidebar.append(toggle)
  return true
}

function isMenuOpen(): boolean {
  return document.body.classList.contains(MENU_CLASS)
}

function setMenuOpen(open: boolean): void {
  document.body.classList.toggle(MENU_CLASS, open)
  const toggle = document.querySelector<HTMLButtonElement>('.kiosk-menu-toggle')
  toggle?.setAttribute('aria-expanded', String(open))
}

function closeMenu(): void {
  setMenuOpen(false)
}

let keydownWired = false

function bindSidebarClose(frame: HTMLElement): void {
  const sidebar = frame.querySelector<HTMLElement>('.app-sidebar')
  sidebar?.addEventListener('click', (event) => {
    const target = event.target as HTMLElement | null
    if (target?.closest('a, button')) {
      closeMenu()
    }
  })
}

function wireGlobal(): void {
  if (keydownWired) return
  keydownWired = true
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeMenu()
  })
}

function menuLabel(): string {
  const lang = document.documentElement.lang
  if (lang === 'uz') return 'Menyu'
  if (lang === 'ru') return 'Меню'
  return 'Menu'
}

function appTitle(): string {
  const lang = document.documentElement.lang
  if (lang === 'uz') return 'Ishchi kioski'
  if (lang === 'ru') return 'Киоск рабочего'
  return 'Worker Kiosk'
}

function injectInto(frame: HTMLElement): boolean {
  if (frame.querySelector('.kiosk-topbar')) return true

  const backdrop = document.createElement('div')
  backdrop.className = 'kiosk-menu-backdrop'
  backdrop.setAttribute('aria-hidden', 'true')

  const topbar = document.createElement('div')
  topbar.className = 'kiosk-topbar'

  const toggle = document.createElement('button')
  toggle.type = 'button'
  toggle.className = 'kiosk-menu-toggle'
  toggle.setAttribute('aria-label', menuLabel())
  toggle.setAttribute('aria-expanded', 'false')
  toggle.innerHTML =
    '<span class="kiosk-menu-icon" aria-hidden="true"></span>' +
    `<span class="kiosk-menu-label">${menuLabel()}</span>`

  const title = document.createElement('span')
  title.className = 'kiosk-topbar-title'
  title.textContent = appTitle()

  topbar.append(toggle, title)
  frame.prepend(backdrop)
  frame.prepend(topbar)

  toggle.addEventListener('click', () => setMenuOpen(!isMenuOpen()))
  backdrop.addEventListener('click', closeMenu)
  bindSidebarClose(frame)
  wireGlobal()

  return true
}

/** Idempotent: keeps the drawer chrome in sync with the app shell. */
export function setupKioskMenu(): void {
  if (typeof document === 'undefined') return

  // Restore the persisted theme before the shell renders (covers login screen).
  applyTheme(currentTheme())

  const tryInject = (): boolean => {
    const frame = document.querySelector<HTMLElement>('.app-frame')
    if (!frame) return false
    injectInto(frame)
    injectThemeToggle(frame)
    return true
  }

  // The shell may already exist (e.g. a restored session on page refresh).
  tryInject()

  // Persistent observer — deliberately never disconnected. The shell is torn
  // down and re-created on every logout → login round trip, and on the very
  // first sign-in it can be injected into and then replaced within a single
  // render. Watching forever guarantees the top bar reappears whenever a
  // `.app-frame` is (re)created. `injectInto` is a no-op once the top bar is
  // present, so repeated calls are harmless.
  const observer = new MutationObserver(() => {
    tryInject()
  })
  observer.observe(document.getElementById('app') ?? document.body, {
    childList: true,
    subtree: true,
  })

  // Polling safety net for the first sign-in: the shell is rendered, injected
  // into, and sometimes replaced all within the same frame — a mutation
  // callback can observe the discarded frame and the top bar is lost. A cheap
  // idempotent poll guarantees it appears even if a mutation is missed.
  window.setInterval(() => {
    tryInject()
  }, 400)
}
